import { useMemo } from "react"
import { useSelector } from "react-redux"
import { Stack, TablePagination, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { HIDDEN_IN_PRINT } from "../../../../../shared/components/print/printPageOnly"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { getMaterialReactTableTranslation } from "../../../../localization/localizationLib"
import { selectSelectedLocale } from "../../../../localization/localizationSelectors"

import { TABLE_PAGE_SIZE_OPTIONS } from "../meetingRoomsLib"
import type { MeetingRoomTablePagination } from "../meetingRoomsTypes"

const useStyles = makeStyles()((theme) => ({
    root: {
        padding: "1rem 0 0",
        borderTop: `1px solid ${theme.palette.tailwind.slate[100]}`,
        ...HIDDEN_IN_PRINT,
    },
}))

type MeetingRoomsTableFooterProps = Readonly<{
    shownRooms: number
    isNoMatch: boolean
    pagination: MeetingRoomTablePagination
    contextLabel: string
    onPageChange: (pageIndex: number) => void
    onPageSizeChange: (pageSize: number) => void
}>

export function MeetingRoomsTableFooter({
    shownRooms,
    isNoMatch,
    pagination: { pageIndex, pageSize },
    contextLabel,
    onPageChange,
    onPageSizeChange,
}: MeetingRoomsTableFooterProps) {
    const { classes } = useStyles()

    const selectedLocale = useSelector(selectSelectedLocale)
    const localization = useMemo(() => getMaterialReactTableTranslation(selectedLocale), [selectedLocale])

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing="1rem"
            className={classes.root}
        >
            {isNoMatch ? (
                <LocalizedStrict id="service-center-meeting-rooms-table-footer-no-match">
                    <Typography variant="body2" color="text.secondary">
                        No rooms match the filters
                    </Typography>
                </LocalizedStrict>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    {contextLabel}
                </Typography>
            )}
            <TablePagination
                component="div"
                count={shownRooms}
                page={pageIndex}
                rowsPerPage={pageSize}
                rowsPerPageOptions={TABLE_PAGE_SIZE_OPTIONS}
                labelRowsPerPage={localization.rowsPerPage}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} ${localization.of} ${count}`}
                getItemAriaLabel={(type) =>
                    type === "next" ? localization.goToNextPage : localization.goToPreviousPage
                }
                onPageChange={(_, page) => onPageChange(page)}
                onRowsPerPageChange={(event) => onPageSizeChange(Number(event.target.value))}
            />
        </Stack>
    )
}
